import "@cva/renderer/fonts.css";
import type { AssetInfo as RendererAsset } from "@cva/renderer";
import { type DocumentDetail, getPreset, referencedAssetIds } from "@cva/shared";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarDays,
  Copy,
  Download,
  FileStack,
  History,
  Keyboard,
  LayoutTemplate,
  Maximize,
  Minus,
  MoreHorizontal,
  Plus,
  Redo2,
  Scaling,
  Search,
  Share2,
  TableProperties,
  Undo2,
} from "lucide-react";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { ActivityTray } from "../components/activity";
import { Mark } from "../components/env-badge";
import { MultiFormatDialog } from "../components/multi-format-dialog";
import { NotificationBell } from "../components/notifications";
import { SearchPalette, useSearchShortcut } from "../components/search-palette";
import { Button } from "../components/ui/button";
import {
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "../components/ui/dropdown";
import { client, orpc } from "../lib/api";
import { useOrgFonts } from "../lib/fonts";
import { useOrgRole } from "../lib/org";
import { errorMessage } from "../lib/utils";
import { useBlockActions } from "./BlocksPanel";
import { BulkCreateDialog, bulkCreateCommand } from "./BulkCreateDialog";
import { Canvas, fitView, zoomAt } from "./Canvas";
import {
  CommentsButton,
  CommentsPanel,
  CommentUiProvider,
  ShareDialog,
  useCommentUi,
  useCommentUiState,
} from "./Collaboration";
import { addAsset } from "./commands";
import { useCutoutLifecycle } from "./cutout";
import { DesignCheckButton, designCheckSearchAction } from "./DesignCheck";
import { TransferDialog, VersionsDialog } from "./Dialogs";
import { ExportDialog } from "./ExportDialog";
import {
  type EditorEnv,
  EditorEnvContext,
  toAssetInfo,
  useEditorAssets,
  useEditorBrand,
  useEnv,
  useUploadAndInsert,
} from "./env";
import { FindReplace } from "./FindReplace";
import { useShortcuts } from "./hooks";
import { ViewMenu } from "./LayoutPanel";
import { LeftPanel, LeftRail } from "./LeftPanel";
import { LiveClosedBanner, LiveIndicator, PeerAvatars } from "./LivePeers";
import { useLeftNav } from "./left-nav";
import { LiveContext, LiveSync } from "./live";
import { BoardToggle } from "./PageSettings";
import { PresentButton } from "./PresentButton";
import { PropertiesPanel } from "./PropertiesPanel";
import { SaveBlockDialog } from "./SaveBlockDialog";
import { openShortcuts, ShortcutsDialog } from "./ShortcutsDialog";
import { SplitPagesDialog } from "./SplitPagesDialog";
import {
  createEditorStore,
  EditorContext,
  type EditorStore,
  useEditor,
  useEditorApi,
} from "./store";
import { IconButton } from "./ui";
import { CaptionDataContext, useCaptionData, useMediaProcessingRefresh } from "./video/data";
import { VideoFormatDialog } from "./video/FormatDialog";
import { SpeakerSetup } from "./video/SpeakerSetup";
import { createVideoStore, VideoContext, type VideoStore } from "./video/store";
import { Timeline } from "./video/Timeline";

function TitleInput({ env }: { env: EditorEnv }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<string | null>(null);
  const rename = useMutation(
    orpc.documents.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
        setValue(null);
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  if (!env.canEdit) return <span className="truncate text-sm font-medium">{env.title}</span>;
  return (
    <input
      value={value ?? env.title}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => {
        if (value?.trim() && value.trim() !== env.title) {
          rename.mutate({ orgId: env.orgId, documentId: env.documentId, title: value.trim() });
        } else setValue(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") (event.currentTarget as HTMLInputElement).blur();
        if (event.key === "Escape") {
          setValue(null);
          (event.currentTarget as HTMLInputElement).blur();
        }
      }}
      className="h-8 w-56 min-w-0 truncate rounded-md border border-transparent px-2 text-sm font-medium hover:border-zinc-200 focus:border-brand-500 focus:outline-none"
    />
  );
}

function ZoomControls({
  store,
  viewport,
}: {
  store: EditorStore;
  viewport: React.RefObject<HTMLDivElement | null>;
}) {
  const zoom = useEditor((s) => s.zoom);
  const center = () => {
    const rect = viewport.current?.getBoundingClientRect();
    return rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 };
  };
  return (
    <div className="flex items-center rounded-lg border border-zinc-200 bg-white">
      <IconButton label="Verkleinern" shortcut="⌘−" onClick={() => zoomAt(store, 0.8, center())}>
        <Minus />
      </IconButton>
      <button
        type="button"
        title="Auf 100 % setzen (⌘1)"
        className="w-12 text-center text-xs text-zinc-600 tabular-nums"
        onClick={() => zoomAt(store, 1 / zoom, center())}
      >
        {Math.round(zoom * 100)} %
      </button>
      <IconButton label="Vergrößern" shortcut="⌘+" onClick={() => zoomAt(store, 1.25, center())}>
        <Plus />
      </IconButton>
      <IconButton
        label="Einpassen"
        shortcut="⌘0"
        onClick={() => viewport.current && fitView(store, viewport.current.getBoundingClientRect())}
      >
        <Maximize />
      </IconButton>
    </div>
  );
}

/** ⌘K in the editor: blocks and images go into the open page. */
function EditorSearch({
  open,
  onOpenChange,
  onDesignCheck,
  onBulkCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDesignCheck: () => void;
  /** Design documents: opens „Serie erstellen“. */
  onBulkCreate?: (() => void) | undefined;
}) {
  const store = useEditorApi();
  const env = useEnv();
  const readOnly = useEditor((s) => s.readOnly);
  const isDesign = useEditor((s) => s.doc.kind === "design");
  const blocks = useBlockActions();
  return (
    <SearchPalette
      orgId={env.orgId}
      open={open}
      onOpenChange={onOpenChange}
      editor={
        readOnly
          ? undefined
          : {
              blocks: blocks.blocks,
              savedBlocks: blocks.saved.items,
              insertBlock: blocks.insertBlock,
              insertSaved: blocks.insertSaved,
              insertMedia: (asset) => {
                if (asset.kind !== "image" && asset.kind !== "svg") return false;
                addAsset(store, toAssetInfo(asset));
                return true;
              },
              commands: [
                ...(isDesign ? [designCheckSearchAction(onDesignCheck)] : []),
                ...(onBulkCreate ? [bulkCreateCommand(onBulkCreate)] : []),
              ],
            }
      }
    />
  );
}

/** Comments take the place of the properties panel while open. */
function RightPanel() {
  const ui = useCommentUi();
  return (
    <aside
      className="w-72 shrink-0 overflow-y-auto border-l border-zinc-200 bg-white"
      // Formatting a text selection must not end the text editing.
      data-text-format-scope=""
    >
      {ui?.open ? <CommentsPanel /> : <PropertiesPanel />}
    </aside>
  );
}

/** The design editor for one document. */
export function Editor({ orgId, document: initial }: { orgId: string; document: DocumentDetail }) {
  const { can } = useOrgRole(orgId);
  const canEdit = can("document.update");
  // biome-ignore lint/correctness/useExhaustiveDependencies: one store per document
  const store = useMemo(() => createEditorStore(initial.content, !canEdit), [initial.id]);
  const isVideo = initial.content.kind === "video";
  // biome-ignore lint/correctness/useExhaustiveDependencies: one player state per document
  const video = useMemo(() => (isVideo ? createVideoStore() : null), [initial.id]);
  const viewport = useRef<HTMLDivElement>(null);
  // Each editor starts with the side panel as the user left it in this kind of editor.
  // Runs before the first paint, so the rail never shows the other editor's tabs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per document
  useLayoutEffect(() => useLeftNav.getState().reset(isVideo ? "video" : "design"), [initial.id]);
  const [exportOpen, setExportOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  // Roles load asynchronously: keep the store's read-only flag in sync.
  useEffect(() => {
    store.getState().setReadOnly(!canEdit);
  }, [store, canEdit]);
  return (
    <EditorContext.Provider value={store}>
      <VideoContext.Provider value={video}>
        <EditorInner
          orgId={orgId}
          initial={initial}
          store={store}
          video={video}
          canEdit={canEdit}
          canExport={can("document.export")}
          viewport={viewport}
          exportOpen={exportOpen}
          setExportOpen={setExportOpen}
          versionsOpen={versionsOpen}
          setVersionsOpen={setVersionsOpen}
        />
      </VideoContext.Provider>
    </EditorContext.Provider>
  );
}

interface InnerProps {
  orgId: string;
  initial: DocumentDetail;
  store: EditorStore;
  video: VideoStore | null;
  canEdit: boolean;
  canExport: boolean;
  viewport: React.RefObject<HTMLDivElement | null>;
  exportOpen: boolean;
  setExportOpen: (open: boolean) => void;
  versionsOpen: boolean;
  setVersionsOpen: (open: boolean) => void;
}

function EditorInner(props: InnerProps) {
  const { orgId, initial, store, viewport, video } = props;
  const doc = useEditor((s) => s.doc);
  const canUndo = useEditor((s) => s.past.length > 0 && !s.readOnly);
  const canRedo = useEditor((s) => s.future.length > 0 && !s.readOnly);
  const loadedKit = useEditorBrand(orgId, initial.projectId, store);
  const brandKit = loadedKit ?? null;
  const brandMissing = loadedKit === null;
  const assets = useEditorAssets(orgId, doc, brandKit);
  useOrgFonts(orgId);
  const uploadAndInsert = useUploadAndInsert(orgId, initial.projectId, store, video);
  const captionData = useCaptionData(orgId, doc);
  useMediaProcessingRefresh(orgId, assets, doc);
  useCutoutLifecycle();
  const resolveAsset = useCallback(
    (assetId: string): RendererAsset | undefined => {
      const asset = assets.get(assetId);
      return asset ? { url: asset.url, width: asset.width, height: asset.height } : undefined;
    },
    [assets],
  );
  const title = initial.title;
  const env: EditorEnv = useMemo(
    () => ({
      orgId,
      documentId: initial.id,
      projectId: initial.projectId,
      title,
      canEdit: props.canEdit,
      canExport: props.canExport,
      brandKit,
      brandMissing,
      assets,
      resolveAsset,
      uploadAndInsert,
    }),
    [
      orgId,
      initial.id,
      initial.projectId,
      title,
      props.canEdit,
      props.canExport,
      brandKit,
      brandMissing,
      assets,
      resolveAsset,
      uploadAndInsert,
    ],
  );
  const queryClient = useQueryClient();
  // Live collaboration: changes of everyone, who is where (docs/17-live-kollaboration.md).
  // biome-ignore lint/correctness/useExhaustiveDependencies: one connection per document
  const live = useMemo(
    () =>
      new LiveSync(store, {
        orgId,
        documentId: initial.id,
        onMeta: () => void queryClient.invalidateQueries({ queryKey: orpc.documents.key() }),
        onComments: () => void queryClient.invalidateQueries({ queryKey: orpc.comments.key() }),
      }),
    [store],
  );
  useEffect(() => {
    live.start();
    return () => live.stop();
  }, [live]);
  useVideoPresence(video, live);
  const whenApplied = useCallback(() => live.whenApplied(), [live]);
  const navigate = useNavigate();
  const [transferOpen, setTransferOpen] = useState(false);
  const [multiFormatOpen, setMultiFormatOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [videoFormatOpen, setVideoFormatOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [isTemplate, setIsTemplate] = useState(initial.isTemplate);
  const toggleTemplate = async () => {
    try {
      const updated = await client.documents.update({
        orgId,
        documentId: initial.id,
        isTemplate: !isTemplate,
      });
      setIsTemplate(updated.isTemplate);
      void queryClient.invalidateQueries({ queryKey: orpc.templates.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.documents.list.key() });
      toast.success(
        updated.isTemplate
          ? "Als Vorlage markiert – sie erscheint jetzt unter „Vorlagen“"
          : "Nicht mehr als Vorlage markiert",
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  const { comment: commentParam } = useSearch({ strict: false }) as { comment?: string };
  const commentUi = useCommentUiState(commentParam ?? null);
  const [searchOpen, setSearchOpen] = useState(false);
  useSearchShortcut(setSearchOpen);
  const [shareOpen, setShareOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const canShare = useOrgRole(orgId).can("share.manage");
  const { setExportOpen } = props;
  const shortcutActions = useMemo(
    () => ({ openExport: () => setExportOpen(true), save: () => void live.save() }),
    [setExportOpen, live],
  );
  useShortcuts(store, env, viewport, shortcutActions, video);
  const preset = getPreset(doc.format.preset);
  const usedAssets = useMemo(() => new Set(referencedAssetIds(doc)), [doc]);

  return (
    <LiveContext.Provider value={live}>
      <EditorEnvContext.Provider value={env}>
        <CommentUiProvider value={commentUi}>
          <CaptionDataContext.Provider value={captionData}>
            <TooltipPrimitive.Provider>
              <div className="flex h-full flex-col bg-zinc-100">
                <header className="flex h-14 shrink-0 items-center gap-2 border-b border-zinc-200 bg-white px-3">
                  <Link
                    to="/o/$orgId"
                    params={{ orgId }}
                    title="Zur Übersicht"
                    className="ml-1 rounded-md p-1 hover:bg-zinc-100"
                  >
                    <Mark className="size-6" title="Kaleido Lab – zur Übersicht" />
                  </Link>
                  <Button asChild variant="ghost" size="icon" title="Zurück zum Projekt">
                    <Link
                      to="/o/$orgId/projects/$projectId"
                      params={{ orgId, projectId: initial.projectId }}
                    >
                      <ArrowLeft />
                    </Link>
                  </Button>
                  <div className="flex min-w-0 flex-col">
                    <TitleInput env={env} />
                    <span className="truncate px-2 text-[11px] whitespace-nowrap text-zinc-400">
                      {initial.projectName} ·{" "}
                      {preset?.label ??
                        (doc.format.preset === "custom"
                          ? `${doc.format.width} × ${doc.format.height} px`
                          : doc.format.preset)}
                    </span>
                  </div>
                  <div className="ml-2 flex items-center">
                    <IconButton
                      label="Rückgängig"
                      shortcut="⌘Z"
                      disabled={!canUndo}
                      onClick={() => store.getState().undo()}
                    >
                      <Undo2 />
                    </IconButton>
                    <IconButton
                      label="Wiederholen"
                      shortcut="⇧⌘Z"
                      disabled={!canRedo}
                      onClick={() => store.getState().redo()}
                    >
                      <Redo2 />
                    </IconButton>
                  </div>
                  <LiveIndicator />
                  <div className="flex-1" />
                  <PeerAvatars />
                  <IconButton label="Suchen" shortcut="⌘K" onClick={() => setSearchOpen(true)}>
                    <Search />
                  </IconButton>
                  <NotificationBell orgId={orgId} align="end" />
                  <DesignCheckButton open={checkOpen} onOpenChange={setCheckOpen} />
                  <CommentsButton />
                  <BoardToggle />
                  <ViewMenu />
                  <ZoomControls store={store} viewport={viewport} />
                  <DropdownMenu>
                    <DropdownTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Weitere Aktionen"
                        title="Versionsverlauf, Tastenkürzel und mehr"
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownTrigger>
                    <DropdownContent>
                      <DropdownItem onSelect={() => props.setVersionsOpen(true)}>
                        <History /> Versionsverlauf …
                      </DropdownItem>
                      <DropdownItem onSelect={openShortcuts}>
                        <Keyboard /> Tastenkürzel{" "}
                        <span className="ml-auto pl-6 text-xs text-zinc-400">?</span>
                      </DropdownItem>
                      {props.canEdit ? (
                        <>
                          <DropdownSeparator />
                          <DropdownItem onSelect={() => void toggleTemplate()}>
                            <LayoutTemplate />
                            {isTemplate ? "Keine Vorlage mehr" : "Als Vorlage markieren"}
                          </DropdownItem>
                          <DropdownItem
                            onSelect={() =>
                              void navigate({
                                to: "/o/$orgId/planning",
                                params: { orgId },
                                search: { plan: initial.id },
                              })
                            }
                          >
                            <CalendarDays /> Einplanen …
                          </DropdownItem>
                          {doc.kind === "design" ? (
                            <>
                              <DropdownItem onSelect={() => setTransferOpen(true)}>
                                <Scaling /> In anderes Format übertragen …
                              </DropdownItem>
                              <DropdownItem onSelect={() => setBulkOpen(true)}>
                                <TableProperties /> Serie erstellen …
                              </DropdownItem>
                              <DropdownItem onSelect={() => setMultiFormatOpen(true)}>
                                <Copy /> In mehrere Formate …
                              </DropdownItem>
                              <DropdownItem onSelect={() => setSplitOpen(true)}>
                                <FileStack /> Seiten als eigene Dateien …
                              </DropdownItem>
                            </>
                          ) : (
                            <DropdownItem onSelect={() => setVideoFormatOpen(true)}>
                              <Scaling /> Format ändern …
                            </DropdownItem>
                          )}
                        </>
                      ) : null}
                    </DropdownContent>
                  </DropdownMenu>
                  <PresentButton />
                  {canShare ? (
                    <Button variant="secondary" onClick={() => setShareOpen(true)}>
                      <Share2 /> Teilen
                    </Button>
                  ) : null}
                  <Button onClick={() => props.setExportOpen(true)}>
                    <Download /> Exportieren
                  </Button>
                </header>
                <LiveClosedBanner />
                <div className="flex min-h-0 flex-1">
                  <LeftRail />
                  <LeftPanel />
                  <main className="flex min-w-0 flex-1 flex-col">
                    <div className="relative min-h-0 flex-1">
                      <Canvas viewportRef={viewport} />
                      {video ? <SpeakerSetup /> : null}
                      {video ? null : <PageSwitcher />}
                      {video ? null : <FindReplace />}
                    </div>
                    {video ? <Timeline /> : null}
                  </main>
                  <RightPanel />
                </div>
              </div>
              <ActivityTray orgId={orgId} assetIds={usedAssets} />
              <SaveBlockDialog />
              <ExportDialog
                open={props.exportOpen}
                onOpenChange={props.setExportOpen}
                beforeExport={whenApplied}
              />
              <VersionsDialog
                open={props.versionsOpen}
                onOpenChange={props.setVersionsOpen}
                beforeRestore={whenApplied}
              />
              <MultiFormatDialog
                orgId={orgId}
                documentId={initial.id}
                title={initial.title}
                currentPreset={doc.format.preset}
                open={multiFormatOpen}
                onOpenChange={setMultiFormatOpen}
                beforeTransfer={whenApplied}
              />
              {doc.kind === "design" ? (
                <SplitPagesDialog
                  open={splitOpen}
                  onOpenChange={setSplitOpen}
                  beforeCreate={whenApplied}
                />
              ) : null}
              <TransferDialog
                open={transferOpen}
                onOpenChange={setTransferOpen}
                beforeTransfer={whenApplied}
              />
              {video ? null : (
                <BulkCreateDialog
                  open={bulkOpen}
                  onOpenChange={setBulkOpen}
                  onExport={() => props.setExportOpen(true)}
                />
              )}
              {video ? (
                <VideoFormatDialog open={videoFormatOpen} onOpenChange={setVideoFormatOpen} />
              ) : null}
              <EditorSearch
                open={searchOpen}
                onOpenChange={setSearchOpen}
                onDesignCheck={() => setCheckOpen(true)}
                onBulkCreate={video || !props.canEdit ? undefined : () => setBulkOpen(true)}
              />
              <ShortcutsDialog video={Boolean(video)} />
              <ShareDialog open={shareOpen} onOpenChange={setShareOpen} beforeShare={whenApplied} />
            </TooltipPrimitive.Provider>
          </CaptionDataContext.Provider>
        </CommentUiProvider>
      </EditorEnvContext.Provider>
    </LiveContext.Provider>
  );
}

/** Playhead and selected clip of the video editor, for the others. */
function useVideoPresence(video: VideoStore | null, live: LiveSync) {
  useEffect(() => {
    if (!video) return;
    let sentTime = -1;
    const send = () => {
      const { time, selectedClip, playing } = video.getState();
      // While playing, a few updates per second are enough (the others animate).
      const rounded = Math.round(time);
      if (!playing || Math.abs(rounded - sentTime) >= 200) sentTime = rounded;
      live.setPresence({ time: sentTime, clip: selectedClip });
    };
    send();
    return video.subscribe((state, previous) => {
      if (
        state.time !== previous.time ||
        state.selectedClip !== previous.selectedClip ||
        state.playing !== previous.playing
      ) {
        send();
      }
    });
  }, [video, live]);
}

function PageSwitcher() {
  const pages = useEditor((s) => s.doc.pages.length);
  const pageIndex = useEditor((s) => s.pageIndex);
  const store = useEditorApiSafe();
  if (pages <= 1 || !store) return null;
  return (
    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-zinc-200 bg-white px-1 py-1 text-xs shadow-md">
      <button
        type="button"
        className="rounded-full px-2 py-1 hover:bg-zinc-100 disabled:opacity-40"
        disabled={pageIndex === 0}
        onClick={() => store.getState().goToPage(pageIndex - 1)}
      >
        ‹
      </button>
      <span className="tabular-nums">
        Seite {pageIndex + 1} / {pages}
      </span>
      <button
        type="button"
        className="rounded-full px-2 py-1 hover:bg-zinc-100 disabled:opacity-40"
        disabled={pageIndex >= pages - 1}
        onClick={() => store.getState().goToPage(pageIndex + 1)}
      >
        ›
      </button>
    </div>
  );
}

function useEditorApiSafe() {
  return useContext(EditorContext);
}
